import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";

const steps = [
    {
        title: "Buyer Inquiry & Specification Review",
        text: "Share your required grade, quantity, destination port, and shipment terms. We confirm availability and current pricing from the sourcing network.",
    },
    {
        title: "Specification & Contract Confirmation",
        text: "Specifications, pricing, payment terms (LC/TT), and shipment terms (FOB/CFR/CIF) are aligned and locked into the final contract.",
    },
    {
        title: "Sourcing from Verified Partner Network",
        text: "Material is allocated from verified partner processors across Rajasthan, including the Niwai sourcing hub.",
    },
    {
        title: "Quality Inspection & COA Coordination",
        text: "Batch-wise quality checks are coordinated, with Certificate of Analysis and third-party inspection (SGS, Geo-Chem, or equivalent) on request.",
    },
    {
        title: "Packing & Container Stuffing",
        text: "DOC is packed in 50 kg PP/HDPE export bags and stuffed at 22–24 MT per 20ft container, photo-documented before sealing.",
    },
    {
        title: "Export Documentation",
        text: "Commercial invoice, packing list, certificate of origin, and phytosanitary/fumigation documents are prepared per destination rules.",
    },
    {
        title: "Customs Clearance & Port Dispatch",
        text: "Cargo is cleared through Indian customs and dispatched via Mundra, Kandla/Deendayal, JNPT/Nhava Sheva, Pipavav, or Hazira.",
    },
    {
        title: "Shipment Tracking & Post-Shipment Support",
        text: "We share vessel status and support document flow until the cargo reaches your destination port — and stay reachable after it lands.",
    },
];

export default function ExportProcess() {
    return (
        <>
            <Seo
                title="Export Process — 8 Steps from Inquiry to Shipment | AXORA PS GLOBAL LLP"
                description="How AXORA PS GLOBAL LLP executes rapeseed/mustard DOC exports from India: inquiry, contract, verified sourcing, COA coordination, stuffing, documentation, customs clearance, and tracking."
                path="/export-process"
                ogImage="/images/og-export-process.jpg"
            />

            <section data-testid="process-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>Export Process</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Our Export Process
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Eight defined steps from first inquiry to post-shipment support — so buyers always know
                            where their cargo stands and who is responsible for the next move.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="process-timeline" className="border-t border-slate-200 bg-white py-20 lg:py-28">
                <div className="mx-auto w-full max-w-4xl px-5 sm:px-8 lg:px-12">
                    <ol className="relative space-y-10 border-l-2 border-steel/25 pl-8 sm:pl-12">
                        {steps.map((step, i) => (
                            <Reveal key={step.title} delay={0.05}>
                                <li data-testid={`process-step-${i + 1}`} className="relative">
                                    <span className="absolute -left-[53px] top-0 flex h-9 w-9 items-center justify-center rounded-full border-2 border-steel bg-white font-display text-sm font-bold text-navy sm:-left-[70px] sm:h-11 sm:w-11 sm:text-base">
                                        {i + 1}
                                    </span>
                                    <div className="rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-steel/50 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)] sm:p-7">
                                        <h2 className="font-display text-xl font-bold tracking-tight text-navy">{step.title}</h2>
                                        <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{step.text}</p>
                                    </div>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                    <Reveal delay={0.1} className="mt-14">
                        <img
                            src="/images/port-dock.jpg"
                            alt="Cargo vessels berthed at an Indian port pier during export loading operations"
                            loading="lazy"
                            className="aspect-[21/9] w-full rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>
                </div>
            </section>

            <CtaBand
                location="process"
                title="Ready to Start Step One?"
                body="Send your specification, quantity, and destination port on WhatsApp. A co-founder reviews every inquiry and responds with availability, pricing, and shipment options."
                label="Request Quote on WhatsApp"
            />
        </>
    );
}
